<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import type { MediaAsset, SoftwareStyle } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'

const store = useShowroomStore()
const customerLabel = ref(store.state.draft?.customerLabel ?? '')
const projectLabel = ref(store.state.draft?.projectLabel ?? '')

watch([customerLabel, projectLabel], ([customer, project]) => {
  store.setDraftLabels(customer ?? '', project ?? '')
})

const items = computed(() => store.state.draft?.items ?? [])
const selectedStyle = computed(() => {
  const index = items.value.findIndex((item) => item.contentType === 'software-style')
  if (index < 0) return null
  const item = items.value[index]
  if (item === undefined) return null
  const entity = store.getEntity(item.entityId)
  return entity?.contentType === 'software-style' ? { item, entity, index } : null
})
const nonStyleItems = computed(() =>
  items.value
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.contentType !== 'software-style')
)
const canGenerate = computed(() => selectedStyle.value !== null)

function imageAsset(mediaId: string): MediaAsset | undefined {
  const asset = store.getAsset(mediaId)
  return asset?.mediaType === 'image' ? asset : undefined
}

function coverAsset(style: SoftwareStyle): MediaAsset | undefined {
  const previewId = style.pagePreviews[0]?.mediaId
  if (previewId !== undefined) {
    const preview = imageAsset(previewId)
    if (preview !== undefined) return preview
  }
  return style.mediaIds
    .map((id) => store.getAsset(id))
    .find((asset): asset is MediaAsset => asset?.mediaType === 'image')
}

function customerPageLabel(label: string) {
  return label.replace(/（编号映射）/g, '')
}

function moveSupportingItem(position: number, direction: -1 | 1) {
  const from = nonStyleItems.value[position]
  const to = nonStyleItems.value[position + direction]
  if (from !== undefined && to !== undefined) store.moveInDraft(from.index, to.index)
}
</script>

<template>
  <section class="basket-layout">
    <div class="basket-main">
      <div class="section-title">
        <div>
          <p class="eyebrow">SOLUTION BASKET</p>
          <h2>本次演示方案</h2>
        </div>
        <button
          v-if="items.length"
          class="button button-ghost button-danger"
          type="button"
          @click="store.clearDraft"
        >
          清空
        </button>
      </div>

      <article v-if="selectedStyle" class="basket-style-package">
        <figure>
          <img
            v-if="coverAsset(selectedStyle.entity)"
            :src="store.mediaUrl(coverAsset(selectedStyle.entity)!)"
            :alt="coverAsset(selectedStyle.entity)!.altText"
          />
          <div v-else class="media-fallback"><strong>UI</strong><span>风格预览</span></div>
        </figure>
        <div class="basket-style-copy">
          <div class="basket-style-kicker">
            <span>已选完整软件风格包</span>
            <b>10 / 10 业务画面</b>
          </div>
          <h3>{{ selectedStyle.entity.title }}</h3>
          <p>{{ selectedStyle.entity.summary }}</p>
          <p class="basket-business-guide-note">10 个业务画面用于客户功能讲解，8 套风格功能一致、视觉不同。</p>
          <div class="basket-style-pages">
            <span v-for="page in selectedStyle.entity.pagePreviews" :key="page.pageKey">
              {{ customerPageLabel(page.pageLabel) }}
            </span>
          </div>
          <div class="basket-style-actions">
            <RouterLink
              class="button button-secondary"
              :to="{ name: 'software-demo', params: { id: selectedStyle.entity.id } }"
            >
              查看 / 更换风格
            </RouterLink>
            <button class="button button-ghost button-danger" type="button" @click="store.removeFromDraft(selectedStyle.item.itemId)">
              移除风格
            </button>
          </div>
        </div>
      </article>

      <div v-else-if="items.length" class="basket-style-required" role="alert">
        <span>01</span>
        <div>
          <strong>请先选定一套完整软件风格</strong>
          <p>方案预览以统一风格为基础；8 套风格均已覆盖监控、能耗、运维、趋势、报警、报表等 10 个业务画面。</p>
        </div>
        <RouterLink class="button button-primary" :to="{ name: 'catalog', params: { section: 'software' } }">
          选择软件风格
        </RouterLink>
      </div>

      <div v-if="nonStyleItems.length" class="basket-supporting-heading">
        <div>
          <span>OPTIONAL CONTENT</span>
          <strong>补充展示内容</strong>
        </div>
        <small>{{ nonStyleItems.length }} 项</small>
      </div>

      <div v-if="nonStyleItems.length" class="basket-list">
        <article v-for="({ item }, position) in nonStyleItems" :key="item.itemId">
          <span class="basket-order">{{ String(position + 1).padStart(2, '0') }}</span>
          <div>
            <small>
              {{ item.configuration?.kind === 'cabinet-layout' ? '机柜配置' : item.contentType }}
            </small>
            <strong>{{ item.title }}</strong>
            <span v-if="item.configuration?.kind === 'cabinet-layout'" class="basket-item-note">
              {{ item.configuration.pointCount }} 点 ·
              {{ item.configuration.layoutTemplateId }}
            </span>
          </div>
          <div class="basket-controls">
            <button
              type="button"
              :disabled="position === 0"
              aria-label="上移"
              @click="moveSupportingItem(position, -1)"
            >
              ↑
            </button>
            <button
              type="button"
              :disabled="position === nonStyleItems.length - 1"
              aria-label="下移"
              @click="moveSupportingItem(position, 1)"
            >
              ↓
            </button>
            <button type="button" aria-label="移除" @click="store.removeFromDraft(item.itemId)">
              ×
            </button>
          </div>
        </article>
      </div>

      <div v-if="!items.length" class="empty-state basket-empty-state">
        <strong>从一套完整软件风格开始</strong>
        <p>先选定客户界面风格，再按需加入硬件、机柜和案例内容。</p>
        <RouterLink class="button button-primary" :to="{ name: 'catalog', params: { section: 'software' } }">
          浏览 8 套软件风格
        </RouterLink>
      </div>
    </div>

    <aside class="basket-sidebar">
      <p class="eyebrow">SESSION LABELS</p>
      <h3>临时项目标识</h3>
      <p>保存在本机方案草稿中，仅用于当前演示，不作为正式客户档案。</p>
      <label>
        <span>客户简称（可选）</span>
        <input v-model="customerLabel" type="text" maxlength="40" placeholder="例如：某商业项目" />
      </label>
      <label>
        <span>项目名称（可选）</span>
        <input v-model="projectLabel" type="text" maxlength="60" placeholder="例如：楼宇自控升级交流" />
      </label>
      <div class="basket-completeness" :class="{ complete: canGenerate }">
        <span>{{ canGenerate ? '方案基础完整' : '方案待完善' }}</span>
        <strong>{{ canGenerate ? '已选择统一软件风格' : '缺少软件风格' }}</strong>
      </div>
      <RouterLink
        class="button button-primary button-block"
        :class="{ disabled: !canGenerate }"
        :aria-disabled="!canGenerate"
        :tabindex="canGenerate ? undefined : -1"
        :to="canGenerate ? { name: 'solution-preview' } : { name: 'basket' }"
      >
        生成方案预览
      </RouterLink>
      <small class="form-note">当前版本生成客户演示预览，不导出 PDF。</small>
    </aside>
  </section>
</template>
